// 화면 18 — In/Out Diff 패널 (D-1 / 5d / 30d 토글).
// 권위: ARCH_DASHBOARD v1.10.3 §4.18 / §6.2, QUANT_SPEC §2.6.1.1 (O-DB-9).
// 임계 (D-1 only): NORMAL ≤ 8 / WATCH 9~15 / ALERT ≥ 16 (NEW_IN 카운트 기준).
//
// v1.10.3:
//   - 백엔드 응답이 lookback_days (정수) 로 변경 → UI 라벨은 daysToLabel 로 매핑.
//   - prev 요청은 정수 (1/5/30) — page 가 LookbackDays 로 전달.

import { memo } from 'react';
import type { ApiError } from '../../api/client';
import {
  bandColor,
  bandSoftBg,
  classifyNewInState,
  daysToLabel,
  labelToDays,
  type DiffEntry,
  type HeldEntry,
  type LeaderStocksDiff,
  type LookbackDays,
  type LookbackLabel,
} from './types';

interface Props {
  data: LeaderStocksDiff | undefined;
  isLoading: boolean;
  error: ApiError | null;
  prevDays: LookbackDays;
  onPrevDaysChange: (d: LookbackDays) => void;
  onSymbolClick: (symbol: string) => void;
}

const LOOKBACK_OPTIONS: LookbackLabel[] = ['D-1', '5d', '30d'];

export const DiffPanel = memo(function DiffPanel({
  data,
  isLoading,
  error,
  prevDays,
  onPrevDaysChange,
  onSymbolClick,
}: Props): JSX.Element {
  const counts = data?.counts ?? { new_in: 0, dropped_out: 0, held: 0 };
  const state = classifyNewInState(counts.new_in, prevDays);
  const activeLabel = daysToLabel(prevDays);

  // 응답이 실어 보낸 lookback_days 가 있으면 응답값을 우선 표시 (확인용),
  // 없으면 요청값으로 fallback.
  const respondedDays = data?.lookback_days ?? prevDays;
  const respondedLabel = daysToLabel(respondedDays);

  return (
    <div className="lst-diff" data-testid="lst-diff-panel">
      <div className="lst-diff__toolbar">
        <span className="lst-filter__label">비교 기준</span>
        <div className="lst-diff__lookback" role="group" aria-label="Lookback 선택">
          {LOOKBACK_OPTIONS.map((opt) => (
            <button
              type="button"
              key={opt}
              className={`lst-diff__lookback-btn${
                opt === activeLabel ? ' lst-diff__lookback-btn--active' : ''
              }`}
              onClick={() => onPrevDaysChange(labelToDays(opt))}
              data-testid={`lst-diff-lookback-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
        {data?.prev_trading_date && (
          <span className="lst-muted">
            prev: <code>{data.prev_trading_date}</code>
            {data && (
              <>
                {' '}
                · <code>{respondedLabel}</code>
              </>
            )}
          </span>
        )}
        {state && (
          <span
            className={`lst-diff__state lst-diff__state--${state}`}
            title="O-DB-9 NEW_IN 임계 (D-1 only): NORMAL ≤8 / WATCH 9~15 / ALERT ≥16"
            data-testid="lst-diff-state"
          >
            {state} (NEW_IN={counts.new_in})
          </span>
        )}
      </div>

      {error && (
        <div className="lst-banner lst-banner--error" data-testid="lst-diff-error">
          diff 로드 실패: {error.message}
        </div>
      )}

      {isLoading && !data && <div className="lst-empty">불러오는 중…</div>}

      {data && !error && (
        <div className="lst-diff__columns">
          <DiffColumn
            title="NEW IN"
            variant="in"
            count={counts.new_in}
            entries={data.new_in}
            onSymbolClick={onSymbolClick}
            emphasized
          />
          <DiffColumn
            title="DROPPED OUT"
            variant="out"
            count={counts.dropped_out}
            entries={data.dropped_out}
            onSymbolClick={onSymbolClick}
          />
          <HeldColumn
            title="HELD"
            count={counts.held}
            entries={data.held}
            onSymbolClick={onSymbolClick}
          />
        </div>
      )}
    </div>
  );
});

interface DiffColumnProps {
  title: string;
  variant: 'in' | 'out';
  count: number;
  entries: DiffEntry[];
  onSymbolClick: (symbol: string) => void;
  emphasized?: boolean;
}

function DiffColumn({
  title,
  variant,
  count,
  entries,
  onSymbolClick,
  emphasized,
}: DiffColumnProps): JSX.Element {
  return (
    <div className={`lst-diff__col lst-diff__col--${variant}`}>
      <h3 className="lst-diff__col-title">
        <span>{title}</span>
        <span className="lst-diff__col-count">{count}</span>
      </h3>
      {entries.length === 0 ? (
        <div className="lst-empty">—</div>
      ) : (
        <ul className="lst-diff__list">
          {entries.map((e) => (
            <li key={e.symbol}>
              <button
                type="button"
                className="lst-diff__entry"
                onClick={() => onSymbolClick(e.symbol)}
                title={`${e.sector} · rank ${e.rank_in_sector}${
                  e.final_score != null ? ` · ${e.final_score.toFixed(2)}` : ''
                }`}
                data-testid={`lst-diff-${variant}-${e.symbol}`}
              >
                <span className="lst-diff__entry-name">
                  {e.name ?? e.symbol}
                  {emphasized && <span className="lst-newin-pill">NEW</span>}
                </span>
                <span className="lst-diff__entry-symbol">{e.symbol}</span>
                <span
                  className="lst-diff__entry-band"
                  style={{ background: bandColor(e.band) }}
                  aria-label={`band ${e.band ?? 'unclassified'}`}
                >
                  {e.band ?? '—'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface HeldColumnProps {
  title: string;
  count: number;
  entries: HeldEntry[];
  onSymbolClick: (symbol: string) => void;
}

function HeldColumn({ title, count, entries, onSymbolClick }: HeldColumnProps): JSX.Element {
  return (
    <div className="lst-diff__col">
      <h3 className="lst-diff__col-title">
        <span>{title}</span>
        <span className="lst-diff__col-count">{count}</span>
      </h3>
      {entries.length === 0 ? (
        <div className="lst-empty">—</div>
      ) : (
        <ul className="lst-diff__list">
          {entries.map((e) => {
            const rankDelta = e.rank_prev - e.rank_today; // 양수 = 순위 상승
            const arrow = rankDelta > 0 ? '↑' : rankDelta < 0 ? '↓' : '—';
            return (
              <li key={e.symbol}>
                <button
                  type="button"
                  className="lst-diff__entry"
                  onClick={() => onSymbolClick(e.symbol)}
                  title={`${e.sector_today} · rank ${e.rank_prev} → ${e.rank_today}`}
                  data-testid={`lst-diff-held-${e.symbol}`}
                >
                  <span className="lst-diff__entry-name">{e.name ?? e.symbol}</span>
                  <span className="lst-diff__held-meta">
                    {arrow} {e.rank_prev}→{e.rank_today}
                  </span>
                  <span
                    className="lst-diff__entry-band"
                    style={{
                      background: bandColor(e.band_today),
                      // 이전 밴드 표시 (배경 split 효과 — soft bg 로 변화 인지)
                      boxShadow:
                        e.band_today !== e.band_prev
                          ? `inset 0 -3px 0 0 ${bandSoftBg(e.band_prev)}`
                          : undefined,
                    }}
                    aria-label={`band ${e.band_prev} → ${e.band_today}`}
                  >
                    {e.band_today ?? '—'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
